import { Skeleton } from '@doccareplus/ui';

/**
 * Loading placeholders for the portal appointments feature. A single
 * `role="status"` region (with an sr-only label) wraps `aria-hidden` shimmer
 * shaped like the real grouped lists — pending, upcoming, and past
 * appointments — so assistive tech announces "loading" once instead of
 * reading every block.
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A single appointment-card row: date/status header line, detail line, action-row placeholder. */
function AppointmentRowSkeleton() {
  return (
    <div
      data-skeleton-row
      className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
      <Skeleton className="h-3 w-3/5" />
    </div>
  );
}

/** A grouped section: uppercase header line plus a few stacked appointment rows. */
function AppointmentGroupSkeleton({ rows }: { rows: number }) {
  return (
    <div data-skeleton-section className="flex flex-col gap-3" aria-hidden>
      <Skeleton className="h-3 w-40" />
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, i) => (
          <AppointmentRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Appointments page loading state — mirrors the "awaiting confirmation" /
 * "upcoming" column and the "past" column of the real grouped layout.
 */
export function AppointmentsPageSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-9 w-40 rounded-[9px]" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <AppointmentGroupSkeleton rows={2} />
            <AppointmentGroupSkeleton rows={2} />
          </div>
          <AppointmentGroupSkeleton rows={2} />
        </div>
      </div>
    </LoadingRegion>
  );
}
